import React, {useEffect} from "react"
import { connect } from "react-redux"
import BVAInput from "../../../../components/BVAInput/BVAInput";
import {
  generateCurrentCompanyBalanceSheetReport,
  updateBalanceSheetReport
} from "../../../../store/modules/financials/actions";
import FinancialHandler from "../../../../handlers/FinancialHandler";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { rowStyle } from "../../../Dashboard/customStyles";
import FinancialAccount from "../../../../models/FinancialAccount";
import MethodSelection from "./MethodSelection";

function BalanceSheetReport({
  currentCompanyBalanceSheetReport,
  currentCompanyBalanceSheetReportSummary,
  dispatchUpdateBalanceSheetReport,
  dispatchGenerateCurrentCompanyBalanceSheetReport
}) {
  // Mounted
  useEffect(() => {
    dispatchGenerateCurrentCompanyBalanceSheetReport()
  }, [])

  // Methods
  const handleReportItemOnChange = ({ groupName, categoryName, key, value}) => {
    dispatchUpdateBalanceSheetReport({ groupName, categoryName }, { [key]: value })
  }

  const handleAnalystNoteOnChange = ({ groupName, categoryName, value }) => {
    dispatchUpdateBalanceSheetReport({ groupName, categoryName }, { analystNote: value })
  }

  const handleTotalNoteOnChange = ({ value }) => {
    dispatchUpdateBalanceSheetReport({ groupName: "TotalNote" }, { totalNote: value })
  }

  const handleManualityOnChange = (value) => {
    dispatchUpdateBalanceSheetReport({ groupName: "Manuality" }, { manuality: value })
  }

  const hasCategories = () => {
    return currentCompanyBalanceSheetReport.some(report => report.categories.length > 0)
  }

  const handleOnClickGPT = async () => {
    handleTotalNoteOnChange({ value: "Generating..." });
    const note = await FinancialHandler.fetchGPTBookValueNoteGeneration(
      currentCompanyBalanceSheetReportSummary?.assets,
      currentCompanyBalanceSheetReportSummary?.liabilities,
      currentCompanyBalanceSheetReportSummary?.equity
    );
    handleTotalNoteOnChange({ value: note });
  }

  return (
    <div className="inline-block">
      <div className="flex items-center justify-between mb-20">
        <h2 className="sectionHead">Adjusted Book Value</h2>
        <MethodSelection value={currentCompanyBalanceSheetReportSummary?.manuality} handleChange={(e) => handleManualityOnChange(e.target.value)} />
      </div>
      {hasCategories() && (
        <div className="flex items-center w-full">
          <div className="w-72"></div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Balance</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Analyst Tool</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Adjustments</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Adjusted Value</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Liquidation %</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Liquidation Value</label>
          </div>
          <div className="w-50 font-semibold pr-7">
            <label className="whitespace-nowrap">Excess/Non-Operating</label>
          </div>
          <div className="w-96 font-semibold flex justify-center">
            <label className="whitespace-nowrap">Analyst note</label>
          </div>
        </div>
      )}
      {hasCategories() && currentCompanyBalanceSheetReport?.map((data, dataIndex) => {
        //this category array and sort method is used for re-arranging the categories by liquidity
        const categoryArray = data.groupName.includes("Assets") ?
          FinancialAccount.getAssetsCategoryOptions().map((obj) => obj.value) : FinancialAccount.getLiabilitiesCategoryOptions().map((obj) => obj.value);
        return (
          <div key={dataIndex} className="mt-5">
            <div className="mb-20">
              {data?.categories?.sort((a, b) => categoryArray.indexOf(a.categoryValue) - categoryArray.indexOf(b.categoryValue))
                .map((category, categoryIndex) => (
                  <div key={categoryIndex} className="flex items-center mb-2">
                    <div className="w-72 flex items-center pr-2">
                      <label className="whitespace-nowrap text-center">{category.categoryName}</label>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      <label className="whitespace-nowrap">{category?.formattedBalance}</label>
                    </div>
                    <div className="w-40 flex items-center pr-10">
                      <BVAInput
                        width="100%"
                        endAdornment
                        onChange={(value) => handleReportItemOnChange({
                          groupName: data.groupName,
                          categoryName: category.categoryName,
                          key: "analystTool",
                          value
                        })}
                        value={category?.analystTool}
                        endAdornmentLabel="%"/>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      <label className="whitespace-nowrap">{category.adjustmentsFormatted}</label>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      {currentCompanyBalanceSheetReportSummary?.manuality === "AUTO" ? 
                        <label className="whitespace-nowrap">{category.adjustedValueFormatted}</label>
                      :
                        <BVAInput
                          width="100%"
                          startAdornment
                          startAdornmentLabel="$ "
                          value={category.adjustedValueFormatted}
                          onChange={(value) => handleReportItemOnChange({
                            groupName: data.groupName,
                            categoryName: category.categoryName,
                            key: "manualAdjustedValue",
                            value
                          })}
                        />
                      }
                    </div>
                    <div className="w-40 flex items-center pr-10">
                      <BVAInput
                        width="100%"
                        endAdornment
                        value={category?.liquidationPercentage}
                        endAdornmentLabel="%"
                        onChange={(value) => handleReportItemOnChange({
                          groupName: data.groupName,
                          categoryName: category.categoryName,
                          key: "liquidationPercentage",
                          value
                        })}/>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      <label className="whitespace-nowrap">{category.liquidationValueFormatted}</label>
                    </div>
                    <div className="w-50 flex items-center pr-5">
                      <BVAInput
                        width="100%"
                        startAdornment
                        startAdornmentLabel="$ "
                        value={category.excessOrNonOperating}
                        onChange={(value) => handleReportItemOnChange({
                          groupName: data.groupName,
                          categoryName: category.categoryName,
                          key: "excessOrNonOperating",
                          value
                        })}/>
                    </div>
                    <div className="w-96 flex items-center">
                      <TextareaAutosize
                        style={rowStyle.tableComments}
                        aria-label="minimum height"
                        minRows={1}
                        maxRows={1}
                        defaultValue={category.analystNote}
                        onBlur={(e) => handleAnalystNoteOnChange({
                          groupName: data.groupName,
                          categoryName: category.categoryName,
                          value: e.target.value
                        })}
                      />
                    </div>
                  </div>
              ))}
              {data?.categories?.length > 0 && (
                <div className="mt-5">
                  <div className="flex items-center mb-4 bg-neutral-100 p-5 rounded-lg">
                    <div className="w-72 flex items-center">
                      <label className="whitespace-nowrap font-semibold">Total {data?.groupName}</label>
                    </div>
                    <div className="w-80 flex items-center -ml-5">
                      <label className="whitespace-nowrap font-semibold">{data?.totalBalance}</label>
                    </div>
                    <div className="w-40 flex items-center">
                      <label className="whitespace-nowrap font-semibold">{data?.totalAdjustments}</label>
                    </div>
                    <div className="w-40 flex items-center">
                      <label className="whitespace-nowrap font-semibold">{data?.totalAdjustedValue}</label>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      <label className="whitespace-nowrap font-semibold">{data?.totalLiquidation}</label>
                    </div>
                    <div className="w-40 flex items-center pr-2">
                      <label className="whitespace-nowrap font-semibold">{data?.totalLiquidationValue}</label>
                    </div>
                    <div className="w-40 flex items-center pr-2 pl-5">
                      <label className="whitespace-nowrap font-semibold">{data?.totalExcessOrNonOperating}</label>
                    </div>
                    <div className="w-96" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}
      <div className="mt-5 border py-5 w-3/4">
        <div className="flex items-center w-full">
          <div className="w-72"></div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Balance</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Adjustments</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Adjusted Value</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Liquidation Value</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Liquidation %</label>
          </div>
          <div className="w-40 font-semibold">
            <label className="whitespace-nowrap">Excess/Non-Operating</label>
          </div>
        </div>

        <div className="flex items-center px-5 rounded-lg mt-5">
          <div className="w-72 flex items-center">
            <label className="whitespace-nowrap font-semibold">Total Assets</label>
          </div>
          <div className="w-40 flex items-center -ml-5">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalBalanceFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalAdjustmentsFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalAdjustedValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalLiquidationValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalLiquidationFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.assets?.totalExcessOrNonOperatingFormatted}
            </label>
          </div>
        </div>
        <div className="flex items-center mb-4 mt-4 px-5 rounded-lg">
          <div className="w-72 flex items-center">
            <label className="whitespace-nowrap font-semibold">Total Liabilities</label>
          </div>
          <div className="w-40 flex items-center -ml-5">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalBalanceFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalAdjustmentsFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalAdjustedValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalLiquidationValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalLiquidationFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.liabilities?.totalExcessOrNonOperatingFormatted}
            </label>
          </div>
        </div>
        <div className="flex items-center mt-4 px-5 rounded-lg">
          <div className="w-72 flex items-center">
            <label className="whitespace-nowrap font-semibold">Equity Value</label>
          </div>
          <div className="w-40 flex items-center -ml-5">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalBalanceFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalAdjustmentsFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalAdjustedValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalLiquidationValueFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalLiquidationFormatted}
            </label>
          </div>
          <div className="w-40 flex items-center pr-2">
            <label className="whitespace-nowrap">
              {currentCompanyBalanceSheetReportSummary?.equity?.totalExcessOrNonOperatingFormatted}
            </label>
          </div>
        </div>
      </div>

      {/* Total Analyst note */}
      <div className="pt-10 w-3/4">
        <h5 className="noteHead">Analyst Note&nbsp;&nbsp;&nbsp;<button onClick={() => handleOnClickGPT()}>🤖</button></h5>
        <textarea
          className="analystNote px-3 py-1"
          rows={3}
          value={currentCompanyBalanceSheetReportSummary?.totalNote}
          onChange={(e) => handleTotalNoteOnChange({ value: e.target.value })}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  currentCompanyBalanceSheetReport: state.financials.currentCompanyBalanceSheetReport,
  currentCompanyBalanceSheetReportSummary: state.financials.currentCompanyBalanceSheetReportSummary
})

const mapDispatchToProps = (dispatch) => ({
  dispatchGenerateCurrentCompanyBalanceSheetReport() {
    dispatch(generateCurrentCompanyBalanceSheetReport())
  },

  dispatchUpdateBalanceSheetReport({ groupName, categoryName }, {
    analystTool,
    manualAdjustedValue,
    liquidationPercentage,
    excessOrNonOperating,
    analystNote,
    totalNote,
    manuality
  }) {
    dispatch(updateBalanceSheetReport({ groupName, categoryName }, {
      analystTool,
      manualAdjustedValue,
      liquidationPercentage,
      excessOrNonOperating,
      analystNote,
      totalNote,
      manuality
    }))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(BalanceSheetReport)